import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import CheckoutStepper from '../components/CheckoutStepper';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, login } = useAuth();
  const { project, selectedServices, total, cart, fromCart } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  
  // Debug: verificar dados recebidos
  React.useEffect(() => {
    console.log('Checkout - Dados recebidos:', { 
      fromCart, 
      hasCart: !!cart, 
      cartLength: cart?.length,
      hasProject: !!project,
      user: !!user
    });
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Login form
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });

  // Register form
  const [registerForm, setRegisterForm] = useState({
    name: '',
    cpf: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: {
      cep: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: ''
    }
  });

  const [cepLoading, setCepLoading] = useState(false);

  useEffect(() => {
    // Se não tem projeto nem carrinho, voltar para home
    if (!project && !fromCart) {
      navigate('/');
      return;
    }
    
    // Se usuário já está logado e NÃO veio do carrinho (compra direta), ir direto para step 3
    if (user && !fromCart && currentStep === 1) {
      setCurrentStep(3);
    }
    
    // Se veio do carrinho, sempre começar no step 1 (independente de estar logado)
    // O usuário sempre deve ver o resumo dos itens do carrinho primeiro
  }, [project, fromCart, navigate, user]);

  const formatCurrency = (value) => {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleAdvance = () => {
    if (currentStep === 1) {
      // Se usuário está logado, pular Step 2 (Identificação) e ir direto para Step 3 (Pagamento)
      if (user) {
        setCurrentStep(3);
      } else {
        // Se não está logado, ir para Step 2 (Login/Cadastro)
        setCurrentStep(2);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      // Se está no Step 3 e usuário logado, voltar para Step 1 (pula Step 2)
      // Se não está logado, voltar para Step 2
      if (user) {
        setCurrentStep(1);
      } else {
        setCurrentStep(2);
      }
    } else {
      navigate(-1);
    }
  };

  const fetchAddress = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    
    if (cleanCep.length !== 8) {
      return;
    }

    setCepLoading(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data.erro) {
        alert('CEP não encontrado');
        return;
      }

      setRegisterForm(prev => ({
        ...prev,
        address: {
          ...prev.address,
          cep: cleanCep,
          street: response.data.logradouro,
          neighborhood: response.data.bairro,
          city: response.data.localidade,
          state: response.data.uf
        }
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      alert('Erro ao buscar CEP');
    } finally {
      setCepLoading(false);
    }
  };

  const handleCepChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length <= 8) {
      if (value.length > 5) {
        value = value.replace(/(\d{5})(\d)/, '$1-$2');
      }
      setRegisterForm(prev => ({
        ...prev,
        address: { ...prev.address, cep: value }
      }));

      // Auto-fetch when 8 digits are entered
      if (value.replace(/\D/g, '').length === 8) {
        fetchAddress(value);
      }
    }
  };

  const formatCPF = (value) => {
    value = value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d)/, '$1.$2');
      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const formatPhone = (value) => {
    value = value.replace(/\D/g, '');
    if (value.length <= 11) {
      value = value.replace(/(\d{2})(\d)/, '($1) $2');
      value = value.replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/customer/login`, loginForm);
      
      // Use auth context to persist login
      login(response.data.token, response.data.username);
      
      // Advance to payment step
      setCurrentStep(3);
      alert('Login realizado com sucesso!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Validations
    if (registerForm.password !== registerForm.confirmPassword) {
      alert('As senhas não coincidem');
      return;
    }

    if (registerForm.password.length < 8) {
      alert('A senha deve ter pelo menos 8 caracteres');
      return;
    }

    setLoading(true);

    try {
      const registerData = {
        name: registerForm.name,
        cpf: registerForm.cpf.replace(/\D/g, ''),
        phone: registerForm.phone.replace(/\D/g, ''),
        email: registerForm.email,
        password: registerForm.password,
        address: {
          ...registerForm.address,
          cep: registerForm.address.cep.replace(/\D/g, '')
        }
      };

      await axios.post(`${BACKEND_URL}/api/customer/register`, registerData);
      
      // Auto login after registration
      const loginResponse = await axios.post(`${BACKEND_URL}/api/customer/login`, {
        email: registerForm.email,
        password: registerForm.password
      });

      // Use auth context to persist login
      login(loginResponse.data.token, loginResponse.data.username);
      
      // Advance to payment step
      setCurrentStep(3);
      alert('Cadastro realizado com sucesso!');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('customer_token');
      
      if (!token) {
        alert('Você precisa estar logado para continuar');
        setCurrentStep(2);
        return;
      }

      let response;

      // Se vier do carrinho com múltiplos itens, usar endpoint de carrinho
      if (fromCart && cart && cart.length > 0) {
        const cartItems = cart.map(item => ({
          project_id: item.project_id,
          selected_services: item.selected_services
        }));

        response = await axios.post(
          `${BACKEND_URL}/api/create-cart-checkout`,
          { cart_items: cartItems },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        // Limpar carrinho local para usuários não logados
        localStorage.removeItem('guest_cart');
      } else {
        // Checkout de projeto único
        response = await axios.post(
          `${BACKEND_URL}/api/create-payment-preference`,
          {
            project_id: project.id,
            selected_services: selectedServices
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }

      // Abrir Mercado Pago em nova aba
      if (response.data.initPoint) {
        window.open(response.data.initPoint, '_blank');
        
        // Exibir mensagem informativa
        alert('Uma nova aba foi aberta com o pagamento do Mercado Pago. Você pode acompanhar o status do seu pedido em "Meus Pedidos".');
        
        // Redirecionar para Meus Pedidos
        setTimeout(() => {
          navigate('/minha-conta');
        }, 1000);
      }
    } catch (error) {
      console.error('Erro ao criar preferência de pagamento:', error);
      alert(error.response?.data?.detail || 'Erro ao processar pagamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Se não tem projeto E não veio do carrinho, não renderizar
  if (!project && !fromCart) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <CheckoutStepper currentStep={currentStep} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        {/* Step 1: Cart Summary */}
        {currentStep === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Carrinho de compras</h2>
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    className="text-orange-500 hover:text-orange-600"
                  >
                    <ArrowLeft size={20} className="mr-2" />
                    Voltar
                  </Button>
                </div>

                <div className="space-y-4">
                  {fromCart && cart && cart.length > 0 ? (
                    // Mostrar itens do carrinho
                    cart.map((item, index) => (
                      <div key={index} className="border-b pb-4">
                        <h3 className="font-bold text-lg text-gray-800 mb-2">{item.project_name}</h3>
                        
                        {item.selected_services.includes('arquitetonico') && (
                          <div className="flex justify-between items-center py-2 text-sm">
                            <span className="text-gray-600">Projeto Arquitetônico</span>
                            <span className="font-semibold">R$ {formatCurrency(item.project_price)}</span>
                          </div>
                        )}
                        
                        {item.selected_services.includes('interiores') && (
                          <div className="flex justify-between items-center py-2 text-sm">
                            <span className="text-gray-600">Projeto de Interiores</span>
                            <span className="font-semibold">Incluído</span>
                          </div>
                        )}
                        
                        {item.selected_services.includes('rrt') && (
                          <div className="flex justify-between items-center py-2 text-sm">
                            <span className="text-gray-600">RRT + Assessoria Técnica</span>
                            <span className="font-semibold">Incluído</span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : project ? (
                    // Mostrar projeto único
                    <div className="border-b pb-4">
                      <h3 className="font-bold text-lg text-gray-800 mb-2">{project.name}</h3>
                      
                      {selectedServices.includes('arquitetonico') && (
                        <div className="flex justify-between items-center py-2 text-sm">
                          <span className="text-gray-600">Projeto Arquitetônico</span>
                          <span className="font-semibold">R$ {formatCurrency(project.price)}</span>
                        </div>
                      )}
                      
                      {selectedServices.includes('interiores') && project.showInterior && (
                        <div className="flex justify-between items-center py-2 text-sm">
                          <span className="text-gray-600">Projeto de Interiores</span>
                          <span className="font-semibold">R$ {formatCurrency(project.interiorPrice)}</span>
                        </div>
                      )}
                      
                      {selectedServices.includes('rrt') && project.showRRT && (
                        <div className="flex justify-between items-center py-2 text-sm">
                          <span className="text-gray-600">RRT + Assessoria Técnica</span>
                          <span className="font-semibold">R$ {formatCurrency(project.rrtPrice)}</span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="p-6 sticky top-24">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Resumo</h3>
                
                {project && project.showDiscount && project.discount > 0 && (
                  <div className="bg-orange-500 text-white px-4 py-2 rounded-lg mb-4 text-center">
                    <span className="font-bold text-2xl">{project.discount}% OFF</span>
                  </div>
                )}

                <div className="space-y-3 mb-6">
                  {fromCart && cart && cart.length > 0 ? (
                    // Resumo do carrinho
                    <>
                      <div className="flex justify-between text-gray-600">
                        <span>Itens ({cart.length}):</span>
                        <span>R$ {formatCurrency(cart.reduce((sum, item) => sum + item.project_price, 0))}</span>
                      </div>
                      
                      <div className="flex justify-between text-xl font-bold text-gray-800 pt-3 border-t">
                        <span>Total:</span>
                        <span>R$ {formatCurrency(cart.reduce((sum, item) => sum + item.project_price, 0))}</span>
                      </div>

                      <div className="text-sm text-gray-600 text-center pt-2">
                        em até 12x de R$ {formatCurrency(cart.reduce((sum, item) => sum + item.project_price, 0) / 12)} no cartão
                      </div>
                    </>
                  ) : project ? (
                    // Resumo do projeto único
                    <>
                      <div className="flex justify-between text-gray-600">
                        <span>Subtotal:</span>
                        <span>R$ {formatCurrency(total / (1 - (project.discount || 0) / 100))}</span>
                      </div>
                      
                      {project.discount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Descontos:</span>
                          <span>- R$ {formatCurrency((total / (1 - project.discount / 100)) - total)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-xl font-bold text-gray-800 pt-3 border-t">
                        <span>Total:</span>
                        <span>R$ {formatCurrency(total)}</span>
                      </div>

                      <div className="text-sm text-gray-600 text-center pt-2">
                        em até 12x de R$ {formatCurrency(total / 12)} no cartão
                      </div>
                    </>
                  ) : null}
                </div>

                <Button
                  onClick={handleAdvance}
                  className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold"
                >
                  Avançar
                </Button>
              </Card>
            </div>
          </div>
        )}

        {/* Step 2: Login/Register */}
        {currentStep === 2 && (
          <div className="max-w-6xl mx-auto">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="mb-6 hover:text-[#00A86B]"
            >
              <ArrowLeft size={20} className="mr-2" />
              Voltar
            </Button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Register Form */}
              <Card className="p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Ainda não tem uma Conta?</h2>
                <p className="text-gray-600 mb-6">Cadastre-se para continuar com a compra</p>

                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome Completo *
                    </label>
                    <Input
                      required
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      placeholder="Digite seu nome completo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CPF *
                    </label>
                    <Input
                      required
                      value={registerForm.cpf}
                      onChange={(e) => setRegisterForm({ ...registerForm, cpf: formatCPF(e.target.value) })}
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone *
                    </label>
                    <Input
                      required
                      value={registerForm.phone}
                      onChange={(e) => setRegisterForm({ ...registerForm, phone: formatPhone(e.target.value) })}
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-mail *
                    </label>
                    <Input
                      required
                      type="email"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      placeholder="seu@email.com"
                    />
                  </div>

                  {/* Address Fields */}
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold text-gray-800 mb-3">Endereço</h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CEP *
                        </label>
                        <Input
                          required
                          value={registerForm.address.cep}
                          onChange={handleCepChange}
                          placeholder="00000-000"
                          maxLength={9}
                          disabled={cepLoading}
                        />
                        {cepLoading && <p className="text-xs text-gray-500 mt-1">Buscando...</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número *
                        </label>
                        <Input
                          required
                          value={registerForm.address.number}
                          onChange={(e) => setRegisterForm({
                            ...registerForm,
                            address: { ...registerForm.address, number: e.target.value }
                          })}
                          placeholder="123"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rua *
                      </label>
                      <Input
                        required
                        value={registerForm.address.street}
                        onChange={(e) => setRegisterForm({
                          ...registerForm,
                          address: { ...registerForm.address, street: e.target.value }
                        })}
                        placeholder="Nome da rua"
                      />
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Complemento
                      </label>
                      <Input
                        value={registerForm.address.complement}
                        onChange={(e) => setRegisterForm({
                          ...registerForm,
                          address: { ...registerForm.address, complement: e.target.value }
                        })}
                        placeholder="Apto, bloco, etc (opcional)"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Bairro *
                        </label>
                        <Input
                          required
                          value={registerForm.address.neighborhood}
                          onChange={(e) => setRegisterForm({
                            ...registerForm,
                            address: { ...registerForm.address, neighborhood: e.target.value }
                          })}
                          placeholder="Bairro"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Cidade *
                        </label>
                        <Input
                          required
                          value={registerForm.address.city}
                          onChange={(e) => setRegisterForm({
                            ...registerForm,
                            address: { ...registerForm.address, city: e.target.value }
                          })}
                          placeholder="Cidade"
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Estado *
                      </label>
                      <Input
                        required
                        value={registerForm.address.state}
                        onChange={(e) => setRegisterForm({
                          ...registerForm,
                          address: { ...registerForm.address, state: e.target.value.toUpperCase() }
                        })}
                        placeholder="UF"
                        maxLength={2}
                      />
                    </div>
                  </div>

                  {/* Password Fields */}
                  <div className="pt-4 border-t">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Senha * (mínimo 8 caracteres)
                      </label>
                      <div className="relative">
                        <Input
                          required
                          type={showPassword ? 'text' : 'password'}
                          value={registerForm.password}
                          onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                          placeholder="Digite sua senha"
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Confirmar Senha *
                      </label>
                      <div className="relative">
                        <Input
                          required
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={registerForm.confirmPassword}
                          onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                          placeholder="Confirme sua senha"
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold mt-6"
                  >
                    {loading ? 'Cadastrando...' : 'Ir para Pagamento'}
                  </Button>
                </form>
              </Card>

              {/* Login Form */}
              <Card className="p-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Já é cadastrado?</h2>
                <p className="text-gray-600 mb-6">Acesse Sua Conta</p>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      E-mail *
                    </label>
                    <Input
                      required
                      type="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      placeholder="seu@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Senha *
                    </label>
                    <Input
                      required
                      type="password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      placeholder="Digite sua senha"
                    />
                  </div>

                  <div className="text-right">
                    <a href="#" className="text-sm text-orange-500 hover:text-orange-600">
                      Recuperar senha
                    </a>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold"
                  >
                    {loading ? 'Entrando...' : 'Login'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {currentStep === 3 && (
          <div className="max-w-2xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => setCurrentStep(2)}
              className="mb-6 hover:text-[#00A86B]"
            >
              <ArrowLeft size={20} className="mr-2" />
              Voltar
            </Button>

            <Card className="p-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">Pagamento Seguro</h2>
              <p className="text-gray-600 mb-8 text-center">
                Você será redirecionado para o checkout seguro do Mercado Pago
              </p>

              {/* Order Summary */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="font-bold text-lg mb-4">Resumo do Pedido</h3>
                
                <div className="space-y-3">
                  {fromCart && cart && cart.length > 0 ? (
                    // Mostrar itens do carrinho
                    <>
                      {cart.map((item, index) => (
                        <div key={index} className="pb-3 mb-3 border-b">
                          <div className="flex justify-between mb-2">
                            <span className="text-gray-700 font-medium">{item.project_name}</span>
                          </div>
                          {item.selected_services.includes('arquitetonico') && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">• Projeto Arquitetônico</span>
                              <span>R$ {formatCurrency(item.project_price)}</span>
                            </div>
                          )}
                          {item.selected_services.includes('interiores') && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">• Projeto de Interiores</span>
                              <span>Incluído</span>
                            </div>
                          )}
                          {item.selected_services.includes('rrt') && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">• RRT + Assessoria</span>
                              <span>Incluído</span>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-300">
                        <span>Total ({cart.length} projeto{cart.length > 1 ? 's' : ''})</span>
                        <span className="text-[#00A86B]">R$ {formatCurrency(cart.reduce((sum, item) => sum + item.project_price, 0))}</span>
                      </div>
                      <div className="text-sm text-gray-600 text-center pt-2">
                        ou em até 12x de R$ {formatCurrency(cart.reduce((sum, item) => sum + item.project_price, 0) / 12)}
                      </div>
                    </>
                  ) : (
                    // Mostrar projeto único
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-700 font-medium">{project.name}</span>
                      </div>
                      
                      {selectedServices.includes('arquitetonico') && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">• Projeto Arquitetônico</span>
                          <span>R$ {formatCurrency(project.price)}</span>
                        </div>
                      )}
                      
                      {selectedServices.includes('interiores') && project.showInterior && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">• Projeto de Interiores</span>
                          <span>R$ {formatCurrency(project.interiorPrice)}</span>
                        </div>
                      )}
                      
                      {selectedServices.includes('rrt') && project.showRRT && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">• RRT + Assessoria Técnica</span>
                          <span>R$ {formatCurrency(project.rrtPrice)}</span>
                        </div>
                      )}

                      {project.discount > 0 && (
                        <div className="flex justify-between text-green-600 text-sm pt-2 border-t">
                          <span>Desconto ({project.discount}%)</span>
                          <span>- R$ {formatCurrency((total / (1 - project.discount / 100)) - total)}</span>
                        </div>
                      )}
                      
                      <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-300">
                        <span>Total</span>
                        <span className="text-[#00A86B]">R$ {formatCurrency(total)}</span>
                      </div>

                      <div className="text-sm text-gray-600 text-center pt-2">
                        ou em até 12x de R$ {formatCurrency(total / 12)}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-800">
                      <strong>Pagamento 100% seguro</strong> processado pelo Mercado Pago.<br />
                      Aceitamos <strong className="text-[#00A86B]">PIX</strong> e <strong>cartão de crédito</strong>.<br />
                      <strong className="text-[#00A86B]">Parcele em até 12x sem juros no cartão!</strong>
                    </p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handlePayment}
                disabled={loading}
                className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold"
              >
                {loading ? 'Processando...' : 'Ir para Pagamento'}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Ao clicar em &quot;Ir para Pagamento&quot;, uma nova aba será aberta com a página segura de checkout do Mercado Pago. Você poderá acompanhar o status do pedido em &quot;Meus Pedidos&quot;.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;
